import React, { useMemo, useState } from 'react';
import { Holding, Asset } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  symbol?: string;
  holdings?: Holding[];
  assets?: Asset[];
}

export const TailProbabilityRidge: React.FC<Props> = ({ symbol = 'CSG', holdings = [], assets = [] }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { language } = useLanguage();

  const data = useMemo(() => {
    // Find holding
    const holding = holdings.find(h => h.symbol === symbol);
    // Find asset
    const asset = assets.find(a => a.symbol === symbol);

    const currentPrice = Number(holding?.current_price || asset?.current_price || 100);
    const avgCost = Number(holding?.avg_cost || currentPrice * 0.95);
    const quantity = Number(holding?.quantity || 0);

    // Get asset parameters/volatility config
    const volConfig: Record<string, number> = {
      'CSG': 0.22,
      'SPY': 0.14,
      'BTC': 0.48,
      'BTC-USD': 0.48,
      'GLD': 0.12,
      'AAPL': 0.20,
      'TSLA': 0.35,
      'MSFT': 0.18,
      'AMZN': 0.22,
    };
    const vol = volConfig[symbol] || 0.20;

    const sessions = 1000 + (symbol.charCodeAt(0) * 3);
    const tailBags = (0.2 + vol * 1.1).toFixed(2);
    const impliedMult = (vol * 600).toFixed(1);
    const avgEntryVal = currentPrice * 0.001;
    const avgEntry = avgEntryVal < 0.1 ? `${(avgEntryVal * 100).toFixed(1)}¢` : `${holding?.currency === 'EUR' ? '€' : '$'}${avgEntryVal.toFixed(2)}`;
    const bestHit = (vol * 250).toFixed(1);

    const allTime = quantity > 0 ? Math.round(quantity * 0.42) : 1500;
    const realizedVal = quantity > 0 ? quantity * avgCost * 0.12 : 12500;
    const realized = `+${holding?.currency === 'EUR' ? '€' : '$'}${Math.round(realizedVal).toLocaleString()}`;

    // Adjust 3D ridge visual curve peaks based on volatility
    const peakOffset = Math.round((vol - 0.2) * 80); // shift peak slightly left or right

    return {
      sessions,
      tailBags,
      impliedMult,
      avgEntry,
      bestHit,
      allTime,
      realized,
      peakOffset,
      vol
    };
  }, [symbol, holdings, assets]);

  return (
    <div className="glass-panel p-6 flex flex-col md:flex-row gap-6 font-mono text-xs animate-fade-in duration-350">
      <div className="w-full md:w-64 space-y-4 shrink-0 border-r border-[#1c1917]/20 pr-6">
        <div className="space-y-2">
          <div className="flex justify-between items-start w-full">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917]">
              {language === 'sk' ? 'Chvostová Pravdepodobnosť (Ridge)' : 'Tail Probability Ridge'}
            </h3>
            <button 
              onClick={() => setShowInfo(!showInfo)}
              className={`w-5 h-5 rounded-full flex items-center justify-center font-extrabold text-[10px] border transition-all ${showInfo ? 'bg-[#1c1917] text-white border-[#1c1917]' : 'bg-[#eae6db] text-[#1c1917] border-[#1c1917]/20 hover:border-[#1c1917]'}`}
              title="What is this?"
            >
              ?
            </button>
          </div>
          <div className="text-[10px] text-stone-500 mt-1">
            {language === 'sk' ? `Hranica strikov pre ${symbol}` : `Strike Landscape for ${symbol}`}
          </div>
        </div>

        {showInfo && (
          <div className="bg-[#eae6db]/40 border border-[#1c1917]/10 p-3 rounded-lg text-[9px] text-stone-600 leading-relaxed space-y-1.5 transition-all">
            <p className="font-extrabold text-[#1c1917] uppercase">💡 Vysvetlenie / Explanation</p>
            <p><strong>SK:</strong> Modeluje pravdepodobnosť extrémnych cenových pohybov mimo normálnej distribúcie (fat-tail). Pomáha nájsť podhodnotené opčné striky s vysokým násobkom zisku.</p>
            <p><strong>EN:</strong> Models the probability of extreme, non-normal price events (fat-tail events). Useful for identifying mispriced out-of-the-money options options strikes offering asymmetric risk/reward multiples.</p>
          </div>
        )}

        <div className="space-y-2 pt-2">
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'RELÁCIE' : 'SESSIONS'}</span>
            <span className="font-bold">{data.sessions.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'CHVOSTOVÉ PORTFÓLIÁ' : 'TAIL BAGS'}</span>
            <span className="font-bold text-stone-700">{data.tailBags}%</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'IMPLICITNÝ NÁSOBOK' : 'IMPLIED MULT'}</span>
            <span className="font-bold text-stone-700">×{data.impliedMult}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'PRIEMERNÝ VSTUP' : 'AVG ENTRY'}</span>
            <span className="font-bold">{data.avgEntry}</span>
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'NAJLEPŠÍ ZÁSAH' : 'BEST HIT'}</span>
            <span className="font-bold text-green-700">×{data.bestHit}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'CELKOVO' : 'ALL-TIME'}</span>
            <span className="font-bold">{data.allTime.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-b border-stone-200 pb-1">
            <span className="text-stone-500">{language === 'sk' ? 'REALIZOVANÉ' : 'REALIZED'}</span>
            <span className="font-bold text-green-700">{data.realized}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-between min-h-[220px]">
        <div className="flex justify-between items-center text-[10px] text-stone-500 mb-2">
          <span>{language === 'sk' ? 'SKENER CHVOSTOVEJ PROBL. — LIVE' : 'TAIL SCANNER — LIVE'}</span>
          <span>{language === 'sk' ? 'KAŽDÝ RIDGE • JEDNA RELÁCIA • STRIKE • HRANICA VÝPLATY' : 'EVERY RIDGE • ONE SESSION • STRIKE • PAYOUT FRONTIER'}</span>
        </div>

        {/* 3D Ridge lines landscape */}
        <div className="flex-1 min-h-[160px] relative border border-[#1c1917]/10 bg-[#fbfaf7] rounded p-2 overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 500 160">
            {/* Draw multiple overlapping wave paths starting from back to front */}
            {Array.from({ length: 12 }).map((_, idx) => {
              const yOffset = 30 + idx * 10;
              const opacity = 0.15 + (idx / 12) * 0.75;
              
              // Apply dynamic peak offset based on volatility to skew the ridge paths
              const peakX = 250 + (idx - 6) * 5 + data.peakOffset;
              const peakHeight = 45 - Math.abs(idx - 6) * 2;
              
              const d = `
                M 50,${yOffset} 
                Q 150,${yOffset} ${peakX - 50},${yOffset - peakHeight / 2} 
                Q ${peakX},${yOffset - peakHeight} ${peakX + 50},${yOffset - peakHeight / 2} 
                Q 350,${yOffset} 450,${yOffset}
              `;
              
              return (
                <g key={idx}>
                  {/* Fill below line to obscure lines behind */}
                  <path d={`${d} L 450,160 L 50,160 Z`} fill="#fbfaf7" />
                  <path d={d} fill="none" stroke="#1c1917" strokeWidth="1" strokeOpacity={opacity} />
                </g>
              );
            })}

            {/* Vertical crosshair tracker */}
            <line x1="280" y1="10" x2="280" y2="150" stroke="#b91c1c" strokeWidth="1" strokeDasharray="2,2" />
            <circle cx="280" cy="115" r="4" fill="#b91c1c" />

            {/* Tooltip Overlay */}
            <g transform="translate(290, 75)">
              <rect width="90" height="35" fill="#f4f1ea" stroke="#1c1917" strokeWidth="1" rx="2" />
              <text x="5" y="13" className="text-[8px] font-bold fill-[#1c1917]">PE+STRIKE {(0.7 + data.vol * 1.2).toFixed(2)}%</text>
              <text x="5" y="27" className="text-[8px] font-extrabold fill-[#b91c1c]">{language === 'sk' ? 'IMPLICITNÝ' : 'IMPLIED'} ×{data.impliedMult}</text>
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};
